
import React from 'react';
import { CubeIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../../shared/components/badges/StatusBadge';
import TabbedForm from '../../../../shared/components/forms/FormTabs';
import StockExpandRow from '../components/StockExpandRow';
import  StockManagmentExpandRow  from '../components/StockManagmentExpandRow';

import { StockService } from '../../../../shared/services/stockService';
import { SupplierService } from '../../../../shared/services/supplierService';
import apiService from '../../../../shared/services/apiService';
// import { StockManagmentExpandRow } from '..';

/* ----------------------------------------
   SUPPLIER OPTIONS
---------------------------------------- */
export const getSupplierdetailsOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/suppliers');
    
    const suppliers =
      res?.data?.suppliers ||
      res?.data?.data?.suppliers ||
      res?.data ||
      [];

    return suppliers.map(s => ({
      value: s._id || s.id,
      label: s.supplierName || s.name
    }));
  } catch (error) {
    console.error('Failed to load suppliers', error);
    return [];
  }
};

/* ----------------------------------------
   CENTER OPTIONS (using dropdowns API)
---------------------------------------- */
export const getCenterOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/vaccination-centers?isAdmin=true');

    const centers =
      res?.data?.vaccinationCenters ||
      res?.vaccinationCenters ||
      res?.data ||
      [];

    return centers.map(c => ({
      value: c.value || c.id || c._id,
      label: c.label || c.name
    }));
  } catch (error) {
    console.warn('Failed to load centers from dropdowns API', error);
    return [];
  }
};  

/* ----------------------------------------
   VACCINE OPTIONS (using dropdowns API)
---------------------------------------- */
export const getVaccineOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns/vaccines');

    const vaccines =
      res?.data?.vaccines ||
      res?.vaccines ||
      res?.data ||
      [];

    return vaccines.map(v => ({
      value: v.value || v.id || v._id,
      label: v.label || v.name
    }));
  } catch (error) {
    console.warn('Failed to load vaccines from dropdowns API', error);
    return [];
  }
};

export const getStatusOptions = () =>
  Promise.resolve([
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "INACTIVE" },
  ]);

/* ----------------------------------------
   STOCK STATUS OPTIONS FOR FORM (using dropdowns API)
---------------------------------------- */
export const getStockStatusOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns');
    const statuses =
      res?.data?.stockStatuses ||
      res?.stockStatuses ||
      [];

    // Filter out 'ALL' option as it's only for filtering, not for form selection
    return statuses
      .filter(s => s.value !== 'ALL')
      .map(s => ({
        value: s.value,
        label: s.label
      }));
  } catch (error) {
    console.warn('Failed to load stock statuses from dropdowns API', error);
    return [
      { value: 'ACTIVE', label: 'In Stock' },
      { value: 'LOW_STOCK', label: 'Low Stock' },
      { value: 'OUT_OF_STOCK', label: 'Out of Stock' },
      { value: 'EXPIRED', label: 'Expired' },
      { value: 'QUARANTINE', label: 'Quarantine' }
    ];
  }
};

/* ----------------------------------------
   STOCK STATUS OPTIONS FOR FILTER DROPDOWN
---------------------------------------- */
export const getStockFilterStatusOptions = async () => {
  try {
    const res = await apiService.getAll('dropdowns');
    const statuses =
      res?.data?.stockStatuses ||
      res?.stockStatuses ||
      [];

    // Return all statuses except 'ALL' (handled separately in filter UI)
    return statuses
      .filter(s => s.value !== 'ALL')
      .map(s => ({
        value: s.value,
        label: s.label
      }));
  } catch (error) {
    console.warn('Failed to load stock filter statuses from dropdowns API', error);
    return [
      { value: 'ACTIVE', label: 'In Stock' },
      { value: 'LOW_STOCK', label: 'Low Stock' },
      { value: 'OUT_OF_STOCK', label: 'Out of Stock' },
      { value: 'EXPIRED', label: 'Expired' },
      { value: 'QUARANTINE', label: 'Quarantine' }
    ];
  }
};

/* ----------------------------------------
   STOCK MANAGEMENT CONFIG
---------------------------------------- */
export const stockManagementConfig = {
  title: 'Vaccine Inventory',
  endpoint: 'admin/vaccine-inventory',
  icon: CubeIcon,

  // Enable URL-based navigation for view/edit/add
  useUrlNavigation: true,
  basePath: '/admin/stock-management',

  // Use stock-specific statuses for filter dropdown (not default user statuses)
  statusOptionsLoader: getStockFilterStatusOptions,

  /* ----------------------------------------
     TABLE COLUMNS
  ---------------------------------------- */
  columns: [
    {
      key: 'vaccineName',
      label: 'Vaccine Name',
      render: item => (
        <div>
          <div className="text-sm font-medium">
            {item.vaccine?.name || item.vaccineName || '—'}
          </div>
          <div className="text-sm opacity-60">
            Batch: {item.batchNumber || '—'}
          </div>
        </div>
      )
    },
    {
      key: 'clinicName',
      label: 'Vaccination Center',
      render: item =>
        item.vaccinationCenter?.name ||
        item.clinicName ||
        '—'
    },
    {
      key: 'quantity',
      label: 'Quantity',
      render: item => (
        <span className="font-medium">
          {item.quantity || 0} units
        </span>
      )
    },
    {
      key: 'totalDoses',
      label: 'Total Doses',
      render: item => (
        <span className="font-medium text-primary-600">
          {item.totalDoses || (item.quantity && item.dosesInVial ? item.quantity * item.dosesInVial : '—')}
        </span>
      )
    },
    {
      key: 'expiryDate',
      label: 'Expiry',
      render: item =>
        item.expiryDate
          ? new Date(item.expiryDate).toLocaleDateString(
              'en-GB',
              { day: '2-digit', month: 'short', year: 'numeric' }
            )
          : '—'
    },
    {
      key: 'status',
      label: 'Status',
      render: item => (
        <StatusBadge status={item.status || 'ACTIVE'} />
      )
    }
  ],

  /* ----------------------------------------
     FORM FIELDS
  ---------------------------------------- */
  formFields: [
    // Basic Info Section
    {
      name: 'vaccineId',
      label: 'Vaccine Name',
      type: 'select',
      optionsLoader: getVaccineOptions,
      required: true,
      section: 'basic',
      renderValue: (value, item) => item?.vaccine?.name || value || '—'
    },
    {
      name: 'clinicId',
      label: 'Vaccination Center',
      type: 'select',
      optionsLoader: getCenterOptions,
      required: true,
      section: 'basic',
      renderValue: (value, item) => item?.vaccinationCenter?.name || value || '—'
    },
    {
      name: 'batchNumber',
      label: 'Batch Number',
      type: 'text',
      required: true,
      section: 'basic'
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      optionsLoader: getStatusOptions,
      default: 'ACTIVE',
      section: 'basic',
      renderValue: (value, item) => {
        return (
          <StatusBadge status={item?.status || value || 'ACTIVE'} />
        )
      }
    },

    // Quantity & Doses Section
    {
      name: 'quantity',
      label: 'Quantity (Vials)',
      type: 'number',
      required: true,
      section: 'doses',
      placeholder: 'Number of vials',
      min: 0
    },
    {
      name: 'dosesInVial',
      label: 'Doses Per Vial',
      type: 'number',
      required: true,
      section: 'doses',
      placeholder: 'e.g. 10',
      min: 1,
      tooltip: 'Enter the number of doses containing in a single vial'
    },
    {
      name: 'remainingFullVials',
      label: 'Remaining Full Vials',
      type: 'number',
      readOnly: true,
      section: 'doses',
      placeholder: 'e.g. 10',
      min: 1
    },
    {
      name: 'totalDoses',
      label: 'Total Doses',
      type: 'number',
      section: 'doses',
      readOnly: true,
      placeholder: 'Auto-calculated',
      renderValue: (value, item) => {
        const total = value || (item?.quantity && item?.dosesInVial ? item.quantity * item.dosesInVial : 0);
        return total ? `${total} doses` : '—';
      }
    },
    {
      name: 'remainingDoses',
      label: 'Remaining Doses',
      type: 'number',
      section: 'doses',
      readOnly: true,
      placeholder: 'Auto-calculated',
    },
    {
      name: 'costPerUnit',
      label: 'Cost Per Unit (₹)',
      type: 'number',
      required: true,
      section: 'doses',
      min: 0,
      tooltip: 'Cost to procure one vial of vaccine'
    },
    {
      name: 'openVialDoses',
      label: 'Open Vial Doses',
      type: 'number',
      section: 'doses',
      placeholder: 'e.g. 10',
      renderValue: (_, item) => {
        return item.openVialDoses ? `${item.openVialDoses} doses` : 0;
      },
    },
    {
      name: 'completedVials',
      label: "Completed Vials",
      type: 'number',
      readOnly: true,
      section: 'doses',
      placeholder: 'e.g. 10',
      renderValue: (_, item) => {
        const val = item.quantity - item.remainingFullVials;
        return val >= 0 ? val : 0;
      },
    },


    // Storage & Dates Section
    {
      name: 'supplierId',
      label: 'Manufacturer',
      type: 'select',
      optionsLoader: getSupplierdetailsOptions,
      required: true,
      section: 'storage',
      renderValue: (value, item) => item?.manufacturer?.supplierName || value || '—'
    },
    {
      name: 'storageLocation',
      label: 'Storage Location',
      type: 'text',
      section: 'storage',
      placeholder: 'e.g. Cold Storage Room 1'
    },
    {
      name: 'temperature',
      label: 'Storage Temperature',
      type: 'text',
      required: true,
      section: 'storage',
      placeholder: 'e.g. 2-8°C'
    },
    {
      name: 'manufacturingDate',
      label: 'Manufacturing Date',
      type: 'date',
      required: true,
      section: 'storage',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      type: 'date',
      required: true,
      section: 'storage',
      renderValue: (value) => value
        ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      optionsLoader: getStockStatusOptions,
      default: 'ACTIVE',
      section: 'storage'
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      rows: 3,
      section: 'storage',
      fullWidth: true
    }
  ],

  searchFields: ['vaccineName', 'batchNumber', 'clinicName'],
  formState: { activeTab: 'basic' },

  /* ----------------------------------------
     CUSTOM HANDLERS
  ---------------------------------------- */
  customHandlers: {
    // Render expanded row content with accordion sections
    renderExpandedRow: (item) => <StockManagmentExpandRow item={item} />,

    transformData: response => {
      const items =
        response?.data?.inventory ||
        response?.data?.data?.inventory ||
        [];

      // Use vaccinationCenter data from inventory response directly (no extra API call needed)
      return items.map(item => ({
        ...item,
        clinicName: item.vaccinationCenter?.name || '—',
        // Calculate totalDoses if not provided
        totalDoses: item.totalDoses || (item.quantity && item.dosesInVial ? item.quantity * item.dosesInVial : null)
      }));
    },

    onView: async item => {
      const res = await StockService.getById(item.id);
      const data = res?.data || res;

      // Map vaccine ID
      if (data.vaccine) {
        data.vaccineId = data.vaccine.id;
      }

      // Map center ID
      if (data.vaccinationCenter) {
        data.clinicId = data.vaccinationCenter.id;
        data.clinicName = data.vaccinationCenter.name;
      }

      // Map manufacturer/supplier ID
      if (data.manufacturer) {
        data.supplierId = data.manufacturer.id || data.manufacturer._id;
      } else if (data.supplier) {
        data.supplierId = data.supplier.id || data.supplier._id;
      }

      // Calculate totalDoses if not provided
      if (!data.totalDoses && data.quantity && data.dosesInVial) {
        data.totalDoses = data.quantity * data.dosesInVial;
      }

      // Calculate completedVials
      if (data.quantity !== undefined && data.remainingFullVials !== undefined) {
         const val = data.quantity - data.remainingFullVials;
         data.completedVials = val >= 0 ? val : 0;
      }

      return data;
    },

    // Auto-calculate totalDoses when quantity or dosesInVial changes
    onFieldChange: (fieldName, value, formData, setFormData) => {
      if (fieldName === 'quantity' || fieldName === 'dosesInVial') {
        const quantity = fieldName === 'quantity' ? parseInt(value) || 0 : parseInt(formData.quantity) || 0;
        const dosesInVial = fieldName === 'dosesInVial' ? parseInt(value) || 0 : parseInt(formData.dosesInVial) || 0;
        const totalDoses = quantity * dosesInVial;
        
        // Calculate completed vials
        const remainingFullVials = formData.remainingFullVials || 0;
        const completedVials = quantity - remainingFullVials;

        // Update totalDoses automatically
        setFormData(prev => ({
          ...prev,
          [fieldName]: value,
          totalDoses: totalDoses > 0 ? totalDoses : '',
          completedVials: completedVials >= 0 ? completedVials : 0
        }));
        return true; // Indicate that we handled the change
      }
      return false; // Let default handler process this field
    },

    renderFormSections: (
      formFields,
      formData,
      handleFormChange,
      mode,
      renderFormField,
      activeTab,
      setActiveTab
    ) => {
      const tabsConfig = {
        basic: { label: 'Basic Info' },
        doses: { label: 'Quantity & Doses' },
        storage: { label: 'Storage & Dates' }
      };

      return (
        <TabbedForm
          formFields={formFields}
          formData={formData}
          handleFormChange={handleFormChange}
          renderFormField={renderFormField}
          tabsConfig={tabsConfig}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    },

    processFormData: formData => {
      const quantity = parseInt(formData.quantity) || 0;
      const dosesInVial = parseInt(formData.dosesInVial) || 0;

      // Exclude calculated fields
      const { completedVials, ...cleanedData } = formData;

      return {
        ...cleanedData,
        quantity,
        dosesInVial,
        totalDoses: quantity * dosesInVial,
        costPerUnit: parseFloat(formData.costPerUnit) || 0,
        remainingDoses: parseInt(formData.remainingDoses) || null,
        remainingFullVials: parseInt(formData.remainingFullVials) || null,
        openVialDoses: parseInt(formData.openVialDoses) || null,
        status: formData.status || 'ACTIVE'
      };
    }
  }
};
